"use client";

import { CalendarDays, Check, MapPin } from "@/components/icons";
import { QuantityStepper } from "@/components/cart/quantity-stepper";
import { DeliveryWindowPicker } from "@/components/cart/delivery-window-picker";
import {
  DeliveryLocationField,
  UNRESOLVED_ADDRESS_MESSAGE,
} from "@/components/cart/delivery-location-field";
import { formatNumber } from "@/lib/formatting/numbers";
import {
  earliestSelectableDeliveryDate,
  formatDeliveryWindow,
} from "@/lib/checkout/delivery-window";
import type { DeliveryLocation } from "@/lib/checkout/delivery-location";
import { Button } from "@/components/ui/button";
import { Chip, ChipCount } from "@/components/ui/chip";
import { siteAccessBrief } from "@/components/logistics/site-access-options";
import { useListingBuyState } from "./listing-buy-state";
import type { ListingQuantityPick } from "./listing-action-state";

const WINDOW_LABEL_ID = "listing-delivery-window-label";
const WINDOW_VALUE_ID = "listing-delivery-window-value";
const ADDRESS_LABEL_ID = "listing-delivery-address-label";
export const INLINE_ADDRESS_INPUT_ID = "listing-delivery-address-inline";

export function ListingBookingBlock({
  unitName,
  unitNamePlural,
}: {
  unitName: string;
  unitNamePlural: string;
}) {
  return (
    <div className="space-y-4">
      <ListingDeliveryAddressRow />
      <ListingQuantityRow unitName={unitName} unitNamePlural={unitNamePlural} />
      <ListingDeliveryWindowRow />
    </div>
  );
}

function ListingQuantityRow({
  unitName,
  unitNamePlural,
}: {
  unitName: string;
  unitNamePlural: string;
}) {
  const {
    quantity,
    setQuantity,
    minQuantity,
    quantityStep,
    quantityPicks,
    setQuantityPending,
  } = useListingBuyState();
  const selectQuantity = (next: number) => {
    setQuantityPending(false);
    setQuantity(next);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <ListingQuantityHeading
          quantity={quantity}
          unitName={unitName}
          unitNamePlural={unitNamePlural}
        />
        <QuantityStepper
          size="lg"
          quantity={quantity}
          minQuantity={minQuantity}
          step={quantityStep}
          onUpdateQuantity={selectQuantity}
          onPendingChange={setQuantityPending}
          className="shrink-0"
        />
      </div>
      <ListingQuantityPicks
        picks={quantityPicks}
        quantity={quantity}
        onSelect={selectQuantity}
      />
    </div>
  );
}

function ListingQuantityHeading({
  quantity,
  unitName,
  unitNamePlural,
}: {
  quantity: number;
  unitName: string;
  unitNamePlural: string;
}) {
  return (
    <p className="min-w-0 text-sm font-semibold">
      Quantity{" "}
      <span className="font-normal text-muted-foreground">
        ({quantity === 1 ? unitName : unitNamePlural})
      </span>
    </p>
  );
}

function ListingQuantityPicks({
  picks,
  quantity,
  onSelect,
}: {
  picks: ListingQuantityPick[];
  quantity: number;
  onSelect: (quantity: number) => void;
}) {
  if (picks.length === 0) return null;
  return (
    <div
      role="group"
      aria-label="Quantity presets"
      className="flex flex-wrap gap-2"
    >
      {picks.map((pick) => (
        <Chip
          key={pick.label}
          asChild
          size="touch"
          tone={pick.quantity === quantity ? "picked" : "outline"}
        >
          <button
            type="button"
            className="flex-1 justify-center"
            aria-pressed={pick.quantity === quantity}
            onClick={() => onSelect(pick.quantity)}
          >
            {pick.quantity === quantity ? <Check aria-hidden="true" className="size-4 text-action" /> : null}
            {pick.label}
            <ChipCount>{formatNumber(pick.quantity)}</ChipCount>
          </button>
        </Chip>
      ))}
    </div>
  );
}

function formatDeliveryAddress(location: DeliveryLocation) {
  const street = [location.line1, location.line2].filter(Boolean).join(", ");
  return `${street}, ${location.city}, ${location.state} ${location.zip}`;
}

function ListingDeliveryAddressRow() {
  const { delivery, siteAccess, openAddressDialog } = useListingBuyState();
  const location = delivery.location;
  const site = siteAccessBrief(siteAccess);

  if (!location?.line1) return <ListingInlineAddressField />;

  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-2">
        <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0">
          <p id={ADDRESS_LABEL_ID} className="text-sm font-semibold">
            Deliver to
          </p>
          <p className="text-sm text-muted-foreground">
            {formatDeliveryAddress(location)}
          </p>
          {site && <p className="text-sm text-foreground">{site}</p>}
        </div>
      </div>
      <Button
        type="button"
        variant="link"
        size="bare"
        className="max-lg:-my-3 max-lg:min-h-11 max-lg:min-w-11"
        aria-describedby={ADDRESS_LABEL_ID}
        onClick={() => openAddressDialog("edit")}
      >
        Change
      </Button>
    </div>
  );
}

function ListingInlineAddressField() {
  const { delivery } = useListingBuyState();
  return (
    <div className="space-y-1.5">
      <DeliveryLocationField
        savedAddressOptions={delivery.savedAddressOptions}
        location={delivery.location}
        addressDisplay={delivery.addressDisplay}
        onSelectSaved={delivery.selectSavedAddress}
        onAddressChange={delivery.onAddressChange}
        onResolvingChange={delivery.onSelectionResolving}
        inputId={INLINE_ADDRESS_INPUT_ID}
        label="Delivery address"
        labelClassName="text-sm font-semibold"
        errorMessage={
          delivery.hasUnresolvedInput ? UNRESOLVED_ADDRESS_MESSAGE : undefined
        }
        compact={false}
      />
      <p className="text-xs text-muted-foreground">
        Add it to see your delivered price.
      </p>
    </div>
  );
}

function ListingDeliveryWindowRow() {
  const { deliveryWindow, setDeliveryWindow, leadTimeDays } =
    useListingBuyState();
  return (
    <div className="space-y-1.5">
      <p id={WINDOW_LABEL_ID} className="text-sm font-semibold">
        Delivery window
      </p>
      <DeliveryWindowPicker
        window={deliveryWindow}
        onWindowChange={setDeliveryWindow}
        labelledBy={WINDOW_LABEL_ID}
        earliestDate={earliestSelectableDeliveryDate(leadTimeDays)}
      >
        <Button
          type="button"
          variant="outline"
          aria-labelledby={`${WINDOW_LABEL_ID} ${WINDOW_VALUE_ID}`}
          className="w-full justify-between font-normal"
        >
          <span id={WINDOW_VALUE_ID}>
            {formatDeliveryWindow(deliveryWindow)}
          </span>
          <CalendarDays className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        </Button>
      </DeliveryWindowPicker>
    </div>
  );
}
